import React from 'react';
import { Info, ChevronDown } from 'lucide-react';

const RobotsTxtDisplay = ({ findings, recommendations = [] }) => {
  if (!findings) return null;

  const { status, aiOptimizeBotAllowed, allowedBots = [], blockedBots = [], content } = findings;

  return (
    <div className="text-left">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white flex items-center">
          Robots.txt Analysis
          {status === 'missing' ? (
            <span className="ml-3 text-red-500 flex items-center text-sm font-normal bg-red-500/10 px-2 py-0.5 rounded border border-red-500/20">
              <span className="mr-1">❌</span> Missing
            </span>
          ) : (
            <span className={`ml-3 flex items-center text-sm font-normal px-2 py-0.5 rounded border ${aiOptimizeBotAllowed ? 'text-green-400 bg-green-500/10 border-green-500/20' : 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20'}`}>
              {aiOptimizeBotAllowed ? (
                <><span className="mr-1">✅</span> AI-OptimizeBot Allowed</>
              ) : (
                <><span className="mr-1">⚠️</span> AI-OptimizeBot Blocked</>
              )}
            </span>
          )}
        </h3>
      </div>

      {status === 'missing' ? (
        <div className="bg-red-950/20 border-l-4 border-red-500 p-4 rounded-r-lg">
          <p className="text-red-300 text-sm">
            No robots.txt file was found in the domain root. This prevents you from guiding AI bots effectively.
          </p>
          {recommendations.find(r => r.type === 'critical')?.template && (
            <div className="mt-3">
              <p className="text-xs font-bold text-red-400 mb-1 uppercase tracking-wider">Recommended Content:</p>
              <pre className="bg-dark-900 p-3 rounded border border-dark-700 text-xs text-dark-100 overflow-x-auto font-mono">
                {recommendations.find(r => r.type === 'critical').template}
              </pre>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-6 mb-6"> {/* Moved up and added mb-6 for spacing */}
            <div>
              <p className="text-[10px] font-bold text-dark-400 uppercase tracking-widest mb-3">Allowed Bots</p>
              <div className="flex flex-wrap gap-2">
                {allowedBots.length > 0 ? (
                  allowedBots.map(bot => (
                    <span key={bot} className="px-2.5 py-1 bg-dark-900 text-green-400 rounded text-xs font-medium border border-dark-700">
                      {bot}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-dark-500 italic">None listed</span>
                )}
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold text-dark-400 uppercase tracking-widest mb-3">Blocked Bots</p>
              <div className="flex flex-wrap gap-2">
                {blockedBots.length > 0 ? (
                  blockedBots.map(bot => (
                    <span key={bot} className="px-2.5 py-1 bg-dark-900 text-red-400 rounded text-xs font-medium border border-dark-700">
                      {bot}
                    </span>
                  ))
                ) : (
                  <span className="text-xs text-dark-500 italic">None listed</span>
                )}
              </div>
            </div>
          </div>

          {!aiOptimizeBotAllowed && (
            <div className="mb-6 bg-primary-500/10 border-l-4 border-primary-500 p-5 rounded-r-lg shadow-inner">
              <h4 className="text-sm font-bold text-primary-400 mb-2 flex items-center gap-2">
                <Info size={16} />
                How to Fix
              </h4>
              <p className="text-dark-200 text-sm mb-3">
                Add these lines to your robots.txt to allow our optimization bot to crawl your site:
              </p>
              <pre className="bg-dark-950 p-4 rounded border border-dark-700 text-xs text-primary-300 overflow-x-auto font-mono shadow-2xl">
{`User-agent: AI-OptimizeBot
Allow: /`}
              </pre>
            </div>
          )}

          <div className="pt-5 border-t border-dark-700 text-xs text-dark-400 leading-relaxed">
            <strong className="text-primary-400 font-bold mr-1">Why this matters:</strong> Think of <code className="bg-dark-900 px-1.5 py-0.5 rounded text-dark-200">robots.txt</code> as your website's "front door." If Answer Engine bots like GPTBot or ClaudeBot are blocked here, they turn away immediately. They will never see your content, and you will have <span className="text-white font-semibold">zero visibility</span> in AI Overviews and citations. Allowing <code className="bg-dark-900 px-1.5 py-0.5 rounded text-dark-200">AI-OptimizeBot</code> ensures we can verify exactly what these engines are seeing.
          </div>

          {content && (
            <div className="mt-4">
              <details className="text-sm border border-dark-700 rounded-lg overflow-hidden group">
                <summary className="cursor-pointer p-3 bg-dark-900 text-dark-300 font-medium hover:bg-dark-750 transition-colors list-none flex items-center justify-between">
                  <span>View Raw robots.txt</span>
                  <ChevronDown size={14} className="group-open:rotate-180 transition-transform" />
                </summary>
                <pre className="p-4 text-xs bg-dark-950 text-dark-200 overflow-x-auto max-h-64 font-mono leading-relaxed border-t border-dark-700">
                  {content}
                </pre>
              </details>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RobotsTxtDisplay;
